import { AxiosResponse } from "axios";
import { ImagePickerAsset } from "expo-image-picker";
import { useCallback, useEffect, useState } from "react";

import { useAxiosClient } from "@/hooks";
import { useFileUpload } from "@/hooks/storage";
import {
  ArchiveAdhocQuestionRequest,
  CreateAdhocQuestionRequest,
  GetAdhocQuestionsRequest,
  UpdateAdhocQuestionRequest,
} from "@/types/requests";
import { AdhocQuestion, Folder } from "@/types/resources";
import {
  CreateAdhocQuestionResponse,
  GetAdhocQuestionResponse,
  GetAdhocQuestionsResponse,
} from "@/types/responses";
import { QuestionType } from "@/types/utils";

const useCreateQuestion = () => {
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const client = useAxiosClient("/adhoc/questions");
  const { uploadFile } = useFileUpload();

  const createQuestion = useCallback(
    async (
      data: Omit<CreateAdhocQuestionRequest, "media"> & {
        media?: ImagePickerAsset[];
      }
    ) => {
      setError(null);
      setLoading(true);
      setUploadProgress(0);

      try {
        let mediaPaths: string[] = [];

        if (data.media && data.media.length > 0) {
          const fileCount = data.media.length;
          const fileProgresses: number[] = new Array(fileCount).fill(0);

          mediaPaths = await Promise.all(
            data.media.map((asset, index) =>
              uploadFile({
                file: asset,
                folder: "adhoc",
                resourceId: data.type,
                onProgress: (progress) => {
                  fileProgresses[index] = progress;
                  const totalProgress =
                    fileProgresses.reduce((sum, p) => sum + p, 0) / fileCount;
                  setUploadProgress(totalProgress);
                },
              })
            )
          );
        }

        const response = await client.post<
          CreateAdhocQuestionResponse,
          AxiosResponse<CreateAdhocQuestionResponse>,
          CreateAdhocQuestionRequest
        >("", {
          folder_id: data.folder_id,
          lines: data.lines.map((line) => line.trim()),
          media: mediaPaths,
          options: data.options.map((opt) => opt.trim()),
          text: data.text?.trim() || null,
          type: data.type,
        });
        return response.data.question;
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
        return null;
      } finally {
        setLoading(false);
        setUploadProgress(0);
      }
    },
    [client, uploadFile]
  );

  return {
    createQuestion,
    error,
    loading,
    uploadProgress,
  };
};

const useQuestions = (
  page: number = 1,
  itemsPerPage: number = 10,
  type?: QuestionType,
  search: string = "",
  folderId?: string,
  archived?: boolean
) => {
  const [count, setCount] = useState<number>(0);
  const [currentFolder, setCurrentFolder] = useState<
    (Folder & { ancestors: Folder[] }) | null
  >(null);
  const [error, setError] = useState<string | null>(null);
  const [folders, setFolders] = useState<Folder[]>([]);
  const [loading, setLoading] = useState(true);
  const [questions, setQuestions] = useState<AdhocQuestion[]>([]);

  const client = useAxiosClient("/adhoc/questions");

  const getQuestions = useCallback(
    (params: GetAdhocQuestionsRequest) => {
      client
        .get<GetAdhocQuestionsResponse>("", { params })
        .then((response) => {
          setCount(response.data.count);
          setCurrentFolder(response.data.current_folder);
          setFolders(response.data.folders);
          setQuestions((prev) =>
            (params.page ?? 1) > 1
              ? [...prev, ...response.data.questions]
              : response.data.questions
          );
          setError(null);
        })
        .catch((e: unknown) => {
          setError(e instanceof Error ? e.message : String(e));
        })
        .finally(() => {
          setLoading(false);
        });
    },
    [client]
  );

  // Reset the list during render (not in an effect) when the filters change.
  // The initial mount needs no reset since the state already starts empty.
  const filterKey = JSON.stringify([search, type, folderId]);
  const [prevFilterKey, setPrevFilterKey] = useState(filterKey);
  if (prevFilterKey !== filterKey) {
    setPrevFilterKey(filterKey);
    setQuestions([]);
    setFolders([]);
    setCurrentFolder(null);
  }

  // Re-enter the loading state during render when any query param changes, so
  // the UI shows loading while refetching.
  const queryKey = JSON.stringify([
    page,
    itemsPerPage,
    type,
    search,
    folderId,
    archived,
  ]);
  const [prevQueryKey, setPrevQueryKey] = useState(queryKey);
  if (prevQueryKey !== queryKey) {
    setPrevQueryKey(queryKey);
    setLoading(true);
    setError(null);
  }

  useEffect(() => {
    getQuestions({
      archived: archived || undefined,
      folder_id: search ? undefined : folderId,
      items_per_page: itemsPerPage,
      page,
      text: search || undefined,
      type,
    });
  }, [page, itemsPerPage, type, search, folderId, archived, getQuestions]);

  const refresh = useCallback(() => {
    setQuestions([]);
    setFolders([]);
    setCurrentFolder(null);
    setLoading(true);
    setError(null);
    getQuestions({
      archived: archived || undefined,
      folder_id: search ? undefined : folderId,
      items_per_page: itemsPerPage,
      page: 1,
      text: search || undefined,
      type,
    });
  }, [getQuestions, itemsPerPage, search, type, folderId, archived]);

  return {
    count,
    currentFolder,
    error,
    folders,
    loading,
    questions,
    refresh,
    setCount,
    setFolders,
    setQuestions,
  };
};

const useQuestion = (questionId: string) => {
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [question, setQuestion] = useState<AdhocQuestion | null>(null);

  const client = useAxiosClient(`/adhoc/questions/${questionId}`);

  const getQuestion = useCallback(() => {
    if (!questionId) return;

    client
      .get<GetAdhocQuestionResponse>("")
      .then((response) => {
        setQuestion(response.data.question);
        setError(null);
      })
      .catch((e: unknown) => {
        setError(e instanceof Error ? e.message : String(e));
      })
      .finally(() => {
        setLoading(false);
      });
  }, [client, questionId]);

  // Re-enter the loading state during render (not in an effect) when the
  // question changes, mirroring the original effect (which only reset once past
  // its `!questionId` early return).
  const [prevQuestionId, setPrevQuestionId] = useState(questionId);
  if (prevQuestionId !== questionId) {
    setPrevQuestionId(questionId);
    if (questionId) {
      setLoading(true);
      setError(null);
    }
  }

  const refresh = useCallback(() => {
    if (questionId) {
      setLoading(true);
      setError(null);
    }
    getQuestion();
  }, [getQuestion, questionId]);

  useEffect(() => {
    getQuestion();
  }, [getQuestion]);

  return {
    error,
    loading,
    question,
    refresh,
    setQuestion,
  };
};

const useUpdateQuestion = () => {
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const client = useAxiosClient("/adhoc/questions");
  const { uploadFile } = useFileUpload();

  const updateQuestion = useCallback(
    async (
      questionId: string,
      questionType: QuestionType,
      data: Omit<UpdateAdhocQuestionRequest, "media"> & {
        media: (ImagePickerAsset | string)[];
      }
    ) => {
      setError(null);
      setLoading(true);
      setUploadProgress(0);

      try {
        const newFiles = data.media.filter((item) => typeof item !== "string");
        const fileCount = newFiles.length;
        const fileProgresses: number[] = new Array(fileCount).fill(0);
        let newFileIndex = 0;

        const mediaPaths = await Promise.all(
          data.media.map(async (item) => {
            if (typeof item === "string") {
              return item;
            }
            const currentIndex = newFileIndex++;
            return uploadFile({
              file: item,
              folder: "adhoc",
              resourceId: questionType,
              onProgress: (progress) => {
                fileProgresses[currentIndex] = progress;
                const totalProgress =
                  fileCount > 0
                    ? fileProgresses.reduce((sum, p) => sum + p, 0) / fileCount
                    : 0;
                setUploadProgress(totalProgress);
              },
            });
          })
        );

        const response = await client.put<
          CreateAdhocQuestionResponse,
          AxiosResponse<CreateAdhocQuestionResponse>,
          UpdateAdhocQuestionRequest
        >(`/${questionId}`, {
          folder_id: data.folder_id,
          lines: data.lines.map((line) => line.trim()),
          media: mediaPaths,
          options: data.options.map((opt) => opt.trim()),
          text: data.text.trim(),
        });
        return response.data.question;
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
        return null;
      } finally {
        setLoading(false);
        setUploadProgress(0);
      }
    },
    [client, uploadFile]
  );

  return {
    error,
    loading,
    updateQuestion,
    uploadProgress,
  };
};

const useArchiveQuestion = () => {
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const client = useAxiosClient("/adhoc/questions");

  const archiveQuestion = useCallback(
    async (questionId: string, archived: boolean) => {
      setError(null);
      setLoading(true);

      try {
        const response = await client.patch<
          GetAdhocQuestionResponse,
          AxiosResponse<GetAdhocQuestionResponse>,
          ArchiveAdhocQuestionRequest
        >(`/${questionId}`, { archived });
        return response.data.question;
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [client]
  );

  return {
    archiveQuestion,
    error,
    loading,
  };
};

const useDeleteQuestion = () => {
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const client = useAxiosClient("/adhoc/questions");

  const deleteQuestion = useCallback(
    async (questionId: string) => {
      setError(null);
      setLoading(true);

      try {
        await client.delete(`/${questionId}`);
        return true;
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      } finally {
        setLoading(false);
      }
    },
    [client]
  );

  return {
    deleteQuestion,
    error,
    loading,
  };
};

export {
  useArchiveQuestion,
  useCreateQuestion,
  useDeleteQuestion,
  useQuestion,
  useQuestions,
  useUpdateQuestion,
};
